import { createNativeStackNavigator } from "@react-navigation/native-stack";
import {
  AppStateController,
  FlashMessagesProvider,
  OnlineStatusNotifier,
  createStackHeaderNavigator,
  AuthProvider,
  MeGuard,
} from "@towersystems/roam-common-native";
import { useFonts } from "expo-font";
import * as Localization from "expo-localization";
import * as SplashScreen from "expo-splash-screen";
import i18n from "i18next";
import { useCallback, useEffect, useState } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";
import { View } from "react-native-ui-lib";
import en from "../../../assets/locales/en.json";
import useCachedResources from "../../../hooks/useCachedResources";
import { BarcodeSettingsProvider } from "../../modules/barcode-scanner/components";
import { RootStack } from "../../modules/landing";
import { LogInScreen, EurekaOAuthCallbackScreen } from "../../modules/login";
import {
  APP_STATE_KEY,
  defaultAppState,
} from "../../utilities/app-state/constants";
import {
  CameraPermission,
  PermissionsManager,
} from "../../utilities/permissions";
import { GlobalSettingsController } from "../GlobalSettingsController";
import { StreamProvider } from "../StreamProvider";
import { useAuthentication } from "@towersystems/roam-common-native/lib/utilities/authentication";
import { gql, useMutation } from "@apollo/client";

const LOGOUT_MUTATION = gql`
  mutation Logout {
    logout {
      success
    }
  }
`;

const translations = {
  en,
};

// Initialize i18next with react-i18next
i18n.use(initReactI18next).init({
  resources: translations,
  lng: Localization.locale,
  fallbackLng: "en",
});

export function App() {
  const isLoadingComplete = useCachedResources();
  const [logoutMutation] = useMutation(LOGOUT_MUTATION);
  const authentication = useAuthentication({
    onLogout: () => { logoutMutation().catch(() => {}); },
  });
  const { bootstraped, bootstrapAsync } = authentication;
  const Stack = createNativeStackNavigator();
  const AuthorizedStack = createStackHeaderNavigator();

  const [, setPermissionsInitialised] = useState(false);

  const initPermissions = async () => {
    try {
      await PermissionsManager.registerManagedPermissions([
        new CameraPermission(),
      ]);

      setPermissionsInitialised(true);
    } catch {
      setPermissionsInitialised(true);
    }
  };

  useEffect(() => {
    initPermissions().catch(() => undefined);
  }, []);

  const [fontsLoaded] = useFonts({
    Roboto: require("../../../assets/fonts/Roboto-Regular.ttf"),
    "Roboto-Bold": require("../../../assets/fonts/Roboto-Bold.ttf"),
    "Roboto-Medium": require("../../../assets/fonts/Roboto-Medium.ttf"),
    "Roboto-Light": require("../../../assets/fonts/Roboto-Light.ttf"),
    "Gilmer-Regular": require("../../../assets/fonts/Gilmer-Regular.ttf"),
    "Gilmer-Bold": require("../../../assets/fonts/Gilmer-Bold.ttf"),
    "Gilmer-Heavy": require("../../../assets/fonts/Gilmer-Heavy.ttf"),
    "Gilmer-Light": require("../../../assets/fonts/Gilmer-Light.ttf"),
    "Gilmer-Medium": require("../../../assets/fonts/Gilmer-Medium.ttf"),
    "Gilmer-Outline": require("../../../assets/fonts/Gilmer-Outline.ttf"),
  });

  useEffect(() => {
    if (false === bootstraped) {
      bootstrapAsync();
    }
  }, [bootstraped]);

  const onLayoutRootView = useCallback(async () => {
    if (fontsLoaded) {
      await SplashScreen.hideAsync();
    }
  }, [fontsLoaded]);

  if (!bootstraped || !isLoadingComplete) {
    return null;
  }

  return (
    <View flex onLayout={onLayoutRootView}>
      <I18nextProvider i18n={i18n}>
        <FlashMessagesProvider>
          <OnlineStatusNotifier />
          <AuthProvider authentication={authentication}>
            {!authentication.isLoggedIn ? (
              <Stack.Navigator
                screenOptions={{
                  headerShown: false,
                }}
              >
                <Stack.Screen name="LogInScreen" component={LogInScreen} />
                <Stack.Screen
                  name="EurekaOAuthCallbackScreen"
                  component={EurekaOAuthCallbackScreen}
                />
              </Stack.Navigator>
            ) : (
              <MeGuard>
                <GlobalSettingsController>
                  <AppStateController
                    stateKey={APP_STATE_KEY}
                    defaultState={defaultAppState}
                  >
                    <BarcodeSettingsProvider>
                      <StreamProvider>
                        <AuthorizedStack.Navigator
                          screenOptions={{ headerShown: false }}
                        >
                          <AuthorizedStack.Screen
                            name="RootStack"
                            component={RootStack}
                          />
                        </AuthorizedStack.Navigator>
                      </StreamProvider>
                    </BarcodeSettingsProvider>
                  </AppStateController>
                </GlobalSettingsController>
              </MeGuard>
            )}
          </AuthProvider>
        </FlashMessagesProvider>
      </I18nextProvider>
    </View>
  );
}
